import { useGetAllShopEmailTemplates } from "@babackapp/private-api-client/client/use-get-all-shop-email-templates";
import { Button } from "@babackapp/ui/components/button";
import { FormField } from "@babackapp/ui/components/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import { useTranslation } from "react-i18next";
import { ExternalLink } from "lucide-react";
import { useWorkflowForm } from "../../context/workflows-editor-workflows-context";
import { useWorkflowsEditorRouting } from "../../context/workflows-editor-routing-context";
import useCurrentShopSuspense from "../../hooks/use-current-shop-suspense";
import { useNodeFormPath } from "../../hooks/use-node-form-path";
import { PanelContent } from "../../lib/panel/panel-content";

export function PanelReturnEmailTemplate() {
  const form = useWorkflowForm();
  const { t } = useTranslation("trackoffice");
  const { data: shop } = useCurrentShopSuspense();
  const { openTemplate } = useWorkflowsEditorRouting();
  const { data: allTemplates = [] } = useGetAllShopEmailTemplates(
    shop.id,
    "return",
  );
  const fieldPath = useNodeFormPath("config.templateId");

  if (!fieldPath) {
    return null;
  }

  const templates = allTemplates?.filter(
    (template) => template.type === "email",
  );

  return (
    <PanelContent
      title={t("workflowsEditor.emailTemplate.title", "Template")}
      description={t(
        "workflowsEditor.returnEmailTemplate.description",
        "The return template used for this email. The action fails if none is selected.",
      )}
    >
      <FormField
        control={form.control}
        name={fieldPath}
        render={({ field }) => {
          const selectedTemplate = templates.find(
            (template) => template.id === field.value,
          );

          return (
            <>
              {/* Always controlled ("" = no selection) and no conditional
                  SelectValue children: swapping undefined children crashes
                  Radix's placeholder node removal (removeChild error). */}
              <Select
                value={(field.value as string | null) ?? ""}
                onValueChange={field.onChange}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue
                    placeholder={t(
                      "workflowsEditor.returnEmailTemplate.placeholder",
                      "Choose a template",
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {templates?.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      <span className="text-xs">{template.title}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {templates.length === 0 && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  {t(
                    "workflowsEditor.returnEmailTemplate.empty",
                    "No return template yet. Create one in Notifications → Templates.",
                  )}
                </p>
              )}

              {selectedTemplate && (
                <div className="flex gap-2 mt-2">
                  {/* V1 scope: no embedded customization sheet — the host
                      navigates to the return templates editor. */}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 gap-2 text-xs"
                    onClick={() => openTemplate?.(selectedTemplate.id)}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    {t(
                      "workflowsEditor.emailNode.customizeTemplate",
                      "Customize template",
                    )}
                  </Button>
                </div>
              )}
            </>
          );
        }}
      />
    </PanelContent>
  );
}
